import React, { useState, useEffect } from 'react';
import DashboardLayout from "../layouts/DashboardLayout";
import InfoModal from "../layouts/InfoModal";
import PaginatedTableContainer from "../containers/PaginatedTableContainer";
import { getAllPartnerPlaceApplications, acceptPartnerPlaceApplication, deletePartnerPlaceApplication } from "../../api/AdminApi";

const PartnerPlaceApplicationManagementPage = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    // 모달 관련 상태
    const [modalOpen, setModalOpen] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);

    const fetchApplications = async (page = 0) => {
        setLoading(true);
        try {
            const response = await getAllPartnerPlaceApplications(page, 20);
            setApplications(response.content);
            setCurrentPage(response.currentPage);
            setTotalPages(response.totalPages);
            setTotalElements(response.totalElements);
        } catch (error) {
            console.error('제휴매장 신청 목록 조회 실패:', error);
            alert('제휴매장 신청 목록을 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (application) => {
        setSelectedApplication(application);
        setModalOpen(true);
    };

    const handleAccept = async () => {
        const confirmed = window.confirm(`정말로 "${selectedApplication.name}" 제휴매장 신청을 수락하시겠습니까?`);
        if (!confirmed) return;
        
        try {
            await acceptPartnerPlaceApplication(selectedApplication.id);
            alert('제휴매장 신청이 수락되었습니다.');
            setModalOpen(false);
            fetchApplications(currentPage);
        } catch (error) {
            console.error('제휴매장 신청 수락 실패:', error);
            alert('제휴매장 신청 수락에 실패했습니다.');
        }
    };

    const handleDelete = async () => {
        const confirmed = window.confirm(`정말로 "${selectedApplication.name}" 제휴매장 신청을 삭제하시겠습니까? 이 동작은 되돌릴 수 없습니다.`);
        if (!confirmed) return;
        
        try {
            await deletePartnerPlaceApplication(selectedApplication.id);
            alert('제휴매장 신청이 삭제되었습니다.');
            setModalOpen(false);
            fetchApplications(currentPage);
        } catch (error) {
            console.error('제휴매장 신청 삭제 실패:', error);
            alert('제휴매장 신청 삭제에 실패했습니다.');
        }
    };

    useEffect(() => {
        fetchApplications();
    }, []);

    const handlePageChange = (page) => {
        if (page >= 0 && page < totalPages) {
            setCurrentPage(page);
            fetchApplications(page);
        }
    };

    // 테이블 컬럼 정의 (PartnerPlaceApplicationResponse 기준)
    const columns = [
        { header: 'id', field: 'id', minWidth: 'min-w-12' },
        { header: 'memberId', field: 'memberId', minWidth: 'min-w-16' },
        { header: 'name', field: 'name', minWidth: 'min-w-32' },
        { 
            header: 'address', 
            field: 'address', 
            minWidth: 'min-w-48',
            render: (application) => {
                if (!application.address) return '';
                return application.address.length > 30 ? `${application.address.substring(0, 30)}...` : application.address;
            }
        },
        { header: 'phone', field: 'phone', minWidth: 'min-w-24' },
        { 
            header: 'latitude', 
            field: 'latitude', 
            minWidth: 'min-w-20',
            render: (application) => application.latitude || ''
        },
        { 
            header: 'longitude', 
            field: 'longitude', 
            minWidth: 'min-w-20',
            render: (application) => application.longitude || ''
        },
        { 
            header: 'policy', 
            field: 'policy', 
            minWidth: 'min-w-32',
            render: (application) => {
                if (!application.policy) return '';
                return application.policy.length > 20 ? `${application.policy.substring(0, 20)}...` : application.policy;
            }
        },
        { 
            header: 'imageId', 
            field: 'imageId', 
            minWidth: 'min-w-16',
            render: (application) => application.imageId || ''
        },
        { header: 'memberPhone', field: 'memberPhone', minWidth: 'min-w-24' }
    ];

    return (
        <DashboardLayout>
            <div className="p-3">
                <h1 className="text-lg font-bold mb-3">제휴매장 신청 관리</h1>

                {/* 상단 정보 */}
                <div className="mb-2 text-xs text-gray-600">
                    총 {totalElements}개 | {currentPage + 1}/{totalPages} 페이지
                </div>

                {/* 페이지네이션 테이블 */}
                <PaginatedTableContainer
                    columns={columns}
                    data={applications}
                    loading={loading}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onRowClick={handleRowClick}
                />

                {/* 제휴매장 신청 상세 모달 */}
                <InfoModal 
                    isOpen={modalOpen} 
                    onClose={() => setModalOpen(false)} 
                    title={`제휴매장 신청 정보 - ${selectedApplication?.name || ''}`}
                    width="w-[700px]"
                >
                    {selectedApplication ? (
                        <div className="text-xs space-y-4">
                            {/* 제휴매장 신청 정보 테이블 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">제휴매장 신청 정보</h3>
                                <table className="w-full border border-gray-400">
                                    <tbody>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100 w-24">id</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.id}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberId</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.memberId}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">name</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.name}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">address</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.address}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">phone</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.phone}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">latitude</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.latitude || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">longitude</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.longitude || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">policy</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.policy || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">imageId</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.imageId || ''}</td></tr>
                                        <tr><td className="border border-gray-400 px-2 py-1 bg-gray-100">memberPhone</td><td className="border border-gray-400 px-2 py-1">{selectedApplication.memberPhone}</td></tr>
                                    </tbody>
                                </table>
                            </div>
                            
                            {/* 제휴매장 이미지 */}
                            {selectedApplication.imageUrl && (
                                <div>
                                    <h4 className="font-bold mb-2 text-sm">제휴매장 이미지</h4>
                                    <img 
                                        src={selectedApplication.imageUrl} 
                                        alt="제휴매장 이미지" 
                                        className="max-w-full max-h-64 border border-gray-300"
                                    />
                                </div>
                            )}

                            {/* 신청 관리 버튼 */}
                            <div>
                                <h3 className="font-bold mb-2 text-sm">신청 관리</h3>
                                <div className="flex gap-2">
                                    <button 
                                        onClick={handleAccept}
                                        className="px-3 py-1 bg-green-200 border border-gray-400 hover:bg-green-300 text-green-800"
                                    >
                                        신청 수락
                                    </button>
                                    <button 
                                        onClick={handleDelete}
                                        className="px-3 py-1 bg-red-200 border border-gray-400 hover:bg-red-300 text-red-800"
                                    >
                                        신청 삭제
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </InfoModal>
            </div>
        </DashboardLayout>
    );
};

export default PartnerPlaceApplicationManagementPage;